import React from "react";
import { Link } from "react-router-dom";
import { ShieldOff, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * AccessDenied — the "you can see this page exists, but your persona
 * cannot enter it" experience.
 *
 * This is intentionally calm and informative rather than alarming. The
 * page tells the user *which* persona they're operating as, names the
 * surface they tried to reach, and points them at the CAIO as the
 * canonical permissions owner.
 */
export default function AccessDenied({ surface, surfaceLabel }) {
  const { user } = useAuth();

  return (
    <div
      className="px-8 py-12 max-w-3xl"
      data-testid="access-denied-page"
    >
      <div
        className="flex items-start gap-5 p-6 rounded-md border border-border-subtle bg-raised shadow-e1"
        data-testid="access-denied-card"
      >
        <div className="shrink-0 h-12 w-12 rounded-md bg-risk-100/50 flex items-center justify-center">
          <ShieldOff size={22} className="text-risk-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-risk-700" data-testid="access-denied-eyebrow">
            Access · restricted
          </p>
          <h1 className="text-h2 text-ink-primary mt-1" data-testid="access-denied-title">
            You don&apos;t have permission to view this page.
          </h1>
          <p className="mt-2 text-sm text-ink-secondary">
            Your current persona{" "}
            <span className="font-semibold text-ink-primary" data-testid="access-denied-current-role">
              {user?.role || "Unknown"}
            </span>{" "}
            is not authorised for{" "}
            <span className="font-semibold text-ink-primary" data-testid="access-denied-surface">
              {surfaceLabel || surface || "this surface"}
            </span>
            . If you believe this is an error, contact your CAIO so the
            permission can be reviewed.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700 transition-colors"
              data-testid="access-denied-back-button"
            >
              <ArrowLeft size={14} /> Return to dashboard
            </Link>
            <span className="text-meta text-ink-disabled" data-testid="access-denied-audit-hint">
              This denial is recorded in the audit log.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
